import React from 'react';
import { TrendingUp, TrendingDown, Minus, LineChart } from 'lucide-react';

// Shared Price History / Trend block used inside BOTH the Price-2-Day and the
// Home & Local price-detail screens. Read-only, mobile-first, uncluttered.
//  • 0–1 observation  -> "No price history yet"
//  • 2 observations   -> both dated prices + up/down/same indicator (no graph)
//  • 3+ observations  -> dated list + a small clean SVG sparkline
// The newest observation is clearly tagged as the current/latest price.
// `observations` must be oldest -> newest: [{ date, price, currency }].

const EPS = 0.005;

const Sparkline = ({ values, color }) => {
  const W = 260;
  const H = 56;
  const PAD = 6;
  const n = values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = n === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (n - 1);
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / span);
    return [x, y];
  });
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      className="mt-2"
      style={{ direction: 'ltr' }}
      data-testid="price-trend-sparkline"
      aria-hidden="true"
    >
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === n - 1 ? 4 : 3} fill={i === n - 1 ? color : '#fff'} stroke={color} strokeWidth="2" />
      ))}
    </svg>
  );
};

export const PriceTrend = ({ observations = [], money, labels = {}, loading = false }) => {
  const L = {
    title: 'Price history',
    none: 'No price history yet',
    noneHint: 'More recorded prices for this product at this shop will build a trend here.',
    latest: 'LATEST',
    rose: 'Price rose',
    fell: 'Price fell',
    flat: 'About the same',
    ...labels,
  };

  const Header = (
    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5 mb-2">
      <LineChart className="h-3.5 w-3.5" />{L.title}
    </p>
  );

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid="price-trend">
        {Header}
        <div className="h-4 w-24 rounded bg-slate-200 animate-pulse" />
      </div>
    );
  }

  const obs = (observations || []).filter((o) => o && o.price != null);

  if (obs.length <= 1) {
    return (
      <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid="price-trend">
        {Header}
        <p className="text-sm text-slate-500" data-testid="price-trend-none">{L.none}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">{L.noneHint}</p>
      </div>
    );
  }

  const first = obs[0].price;
  const last = obs[obs.length - 1].price;
  const diff = last - first;
  const dir = diff > EPS ? 'rose' : diff < -EPS ? 'fell' : 'flat';
  // green = cheaper than before (good for the buyer), red = more expensive.
  const color = dir === 'fell' ? '#059669' : dir === 'rose' ? '#e11d48' : '#64748b';
  const DirIcon = dir === 'rose' ? TrendingUp : dir === 'fell' ? TrendingDown : Minus;
  const dirLabel = dir === 'rose' ? L.rose : dir === 'fell' ? L.fell : L.flat;
  const cur = obs[obs.length - 1].currency;

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid="price-trend">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
          <LineChart className="h-3.5 w-3.5" />{L.title}
        </p>
        <span
          className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
          style={{ color, backgroundColor: color + '1a' }}
          data-testid="price-trend-direction"
        >
          <DirIcon className="h-3 w-3" />{dirLabel}
        </span>
      </div>

      {obs.length >= 3 && <Sparkline values={obs.map((o) => o.price)} color={color} />}

      <ul className="mt-2 divide-y divide-slate-100" data-testid="price-trend-list">
        {obs.map((o, i) => {
          const isLast = i === obs.length - 1;
          return (
            <li key={i} className="flex items-center justify-between gap-3 py-1.5 text-sm" data-testid={`price-trend-row-${i}`}>
              <span className="text-slate-500 tabular-nums">{o.date || '—'}</span>
              <span className="flex items-center gap-2">
                {isLast && (
                  <span className="text-[9px] font-black bg-indigo-600 text-white rounded px-1.5 py-0.5" data-testid="price-trend-latest">{L.latest}</span>
                )}
                <span className={`font-bold tabular-nums ${isLast ? 'text-indigo-700' : 'text-slate-700'}`}>
                  {money ? money(o.price, cur) : o.price}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PriceTrend;
